/**
 * The New form modal (R2, screen 07): blank, template and AI in one dialog.
 *
 * Three mode cards across the top — Start blank · Use a template · ✦ Describe
 * it — then the mode's own body, then Name and Folder, then the footer. It
 * merges the three create paths the list used to hold apart (the inline name
 * box, the template gallery and the AI modal) without losing any of them:
 *
 *   * blank keeps Enter-to-create and the folder the form lands in — now a
 *     picker, defaulting to the folder the rail was pointing at (Q2);
 *   * template keeps everything the gallery did (categories, search, the
 *     read-only preview, the gate notes and template PAIRS) — see
 *     `TemplateGallery.tsx`;
 *   * AI keeps every error and upsell state the old modal had, and gains the
 *     month's allowance from the same counter the Billing card meters (D14).
 *     The card is not shown at all when the install has no key; on a plan
 *     without the AI builder the card shows no count and the POST still
 *     answers with the upsell, exactly as before.
 *
 * A plan-limit refusal closes this modal and hands the body to the list, which
 * opens the same PlanLimitBlock modal every create path has always opened.
 */
import { useCallback, useState } from 'react';
import {
  AiAllowance, FormSummary, FormTemplateSummary, createFormApi, createFormFromTemplateApi, createFormWithAiApi,
} from '../../api/forms';
import { FolderSummary } from '../../api/access';
import { PlanLimitBody, planLimitOf } from '../../api/limits';
import { Modal } from '../../components/fs';
import TemplateBrowser, { templateNote } from './TemplateGallery';

export type NewFormMode = 'blank' | 'template' | 'ai';

export default function NewFormModal({
  initialMode, folders, initialFolderId, aiAvailable, ai, onClose, onCreated, onLimit,
}: {
  initialMode: NewFormMode;
  folders: FolderSummary[];
  /** where the rail was pointing; the picker starts there */
  initialFolderId: number | null;
  /** the platform console has a key saved — the only thing that shows the card */
  aiAvailable: boolean;
  /** this month's allowance, null on a plan without the builder or from an older server */
  ai: AiAllowance | null | undefined;
  onClose: () => void;
  onCreated: (form: FormSummary) => void;
  onLimit: (body: PlanLimitBody) => void;
}) {
  const [mode, setMode] = useState<NewFormMode>(initialMode === 'ai' && !aiAvailable ? 'blank' : initialMode);
  const [name, setName] = useState('');
  const [folderId, setFolderId] = useState<number | null>(initialFolderId);
  const [prompt, setPrompt] = useState('');
  const [template, setTemplate] = useState<FormTemplateSummary | null>(null);
  const [templateCount, setTemplateCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const onSelected = useCallback((t: FormTemplateSummary | null) => setTemplate(t), []);
  const onLoaded = useCallback((n: number) => setTemplateCount(n), []);

  const canCreate = !busy && (
    mode === 'blank' ? !!name.trim()
      : mode === 'template' ? !!template
        : !!prompt.trim());

  async function create() {
    if (!canCreate) return;
    setBusy(true); setError('');
    try {
      const typed = name.trim();
      const r = mode === 'blank'
        ? await createFormApi(typed, folderId)
        : mode === 'template'
          ? await createFormFromTemplateApi(template!.id, typed || undefined, folderId)
          : await createFormWithAiApi(prompt.trim(), typed || undefined, folderId);
      onCreated(r.form);
    } catch (err: any) {
      const limit = planLimitOf(err);
      // The refusal opens its own modal over the list, so this one closes first.
      if (limit) { onClose(); onLimit(limit); }
      else setError(err.message || (mode === 'ai' ? 'Could not generate the form' : 'Could not create the form'));
    } finally {
      setBusy(false);
    }
  }

  /** "AI drafts it · 88 left this month" — or no count where the plan has none to count. */
  const aiLine = !ai
    ? 'AI drafts it'
    : ai.allowed === null
      ? 'AI drafts it · unlimited this month'
      : `AI drafts it · ${Math.max(0, ai.allowed - ai.used)} left this month`;

  const modeCard = (key: NewFormMode, title: string, sub: string) => (
    <button
      key={key} type="button" className={`nf-mode${mode === key ? ' on' : ''}`}
      aria-pressed={mode === key} disabled={busy}
      onClick={() => { setMode(key); setError(''); }}
    >
      <b>{title}</b>
      <span>{sub}</span>
    </button>
  );

  const namePlaceholder = mode === 'ai' ? 'Named from the description'
    : mode === 'template' ? (template ? `Named after “${template.name}”` : 'Named after the template')
      : 'Untitled form';

  const note = mode === 'template' ? templateNote(template) : 'Opens as a draft. Nothing goes live until you publish.';

  return (
    <Modal
      title="New form" xl
      // While the AI is drafting, closing would orphan the wait.
      onClose={() => { if (!busy) onClose(); }}
      footer={<>
        <button className="fs-btn primary" disabled={!canCreate} onClick={() => void create()}>
          {busy
            ? (mode === 'ai' ? 'Drafting…' : 'Creating…')
            : (mode === 'ai' ? 'Draft my form ✦' : 'Create form')}
        </button>
        <button className="fs-btn neutral" disabled={busy} onClick={onClose}>Cancel</button>
        <span className="fs-cell-sub nf-foot-note">
          {busy && mode === 'ai' ? 'Writing the questions — this takes a moment.' : note}
        </span>
      </>}
    >
      <div className="nf-modes" role="group" aria-label="How to start">
        {modeCard('blank', 'Start blank', 'An empty page to build on')}
        {modeCard('template', 'Use a template', templateCount === null ? 'Ready-made forms' : `${templateCount} ready-made ${templateCount === 1 ? 'form' : 'forms'}`)}
        {aiAvailable && modeCard('ai', '✦ Describe it', aiLine)}
      </div>

      {error && <div className="ad-err" style={{ marginBottom: 12 }}>{error}</div>}

      {mode === 'ai' && (
        <div className="fs-field">
          <label htmlFor="nf-prompt">What should it collect?</label>
          <textarea
            id="nf-prompt" autoFocus className="fs-input nf-prompt" rows={4} maxLength={4000}
            placeholder="e.g. A catering enquiry: contact details, event date and venue, guest count, cuisine preferences, dietary requirements and a budget range."
            value={prompt} onChange={(e) => setPrompt(e.target.value)} disabled={busy}
          />
        </div>
      )}

      {mode === 'template' && (
        <TemplateBrowser onSelected={onSelected} onLoaded={onLoaded} />
      )}

      <div className="nf-grid">
        <div className="fs-field">
          <label htmlFor="nf-name">Name{mode === 'blank' ? '' : ' (optional)'}</label>
          <input
            id="nf-name" className="fs-input" autoFocus={mode === 'blank'} placeholder={namePlaceholder}
            value={name} onChange={(e) => setName(e.target.value)} disabled={busy}
            onKeyDown={(e) => { if (e.key === 'Enter' && mode !== 'ai') void create(); }}
          />
        </div>
        <div className="fs-field">
          <label htmlFor="nf-folder">Folder</label>
          <select
            id="nf-folder" className="fs-input" value={folderId ?? ''} disabled={busy}
            onChange={(e) => setFolderId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">— Unfiled</option>
            {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
      </div>
    </Modal>
  );
}
